import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { debounceTime, distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import {
  ErpLeaveImportApprovedRequest,
  ErpLeaveImportCheckResultSummariesResponse,
  ErpLeaveImportCheckResultRow,
  ErpLeaveImportCheckResultsResponse,
  ErpLeaveImportCheckSummaryRow,
  ErpLeaveImportComparisonStatus,
  ErpLeaveImportKind,
  ErpLeaveImportLeaveRecordRow,
  ErpLeaveImportLeaveRecordsParams,
  ErpLeaveImportLeaveRecordsResponse,
  ErpLeaveImportPublicationStatus,
  ErpLeaveImportResultDetail,
  ErpLeaveImportResultRow,
  ErpLeaveImportResultsResponse,
  ErpLeaveImportRowResult,
  ErpLeaveImportService,
} from 'src/app/services/api/erp-leave-import/erp-leave-import.service';
import { RoleID } from 'src/utils/enum/role.enum';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { FormImportNewComponent } from 'src/app/pages/import/components/form-import-new/form-import-new.component';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { environment } from 'src/environments/environment';
import {
  createDatatableSortParams,
  Datatable,
  DatatableSortChangeEvent,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { DatatableComponent } from 'src/shared/datatable/datatable.component';
import { SharedModule } from 'src/shared/shared.module';

declare const bootstrap: any;

interface ErpLeaveImportTeacherDataSource extends Datatable {
  data: ErpLeaveImportResultRow[];
  offset: number;
}

interface ErpLeaveImportLeaveRecordDataSource extends Datatable {
  data: ErpLeaveImportLeaveRecordRow[];
  offset: number;
}

interface ErpLeaveImportCheckDataSource extends Datatable {
  data: ErpLeaveImportCheckSummaryRow[];
  offset: number;
}

interface ErpLeaveImportCheckDetailDataSource extends Datatable {
  data: ErpLeaveImportComparisonRow[];
  offset: number;
}

type ErpLeaveImportTab = 'leave_records' | 'check_results' | 'match_results';
type DetailComparisonStatus = ErpLeaveImportComparisonStatus | 'missing_approved_request' | 'source_unmatched';
type ErpLeaveImportComparisonColumnScope = 'combined' | 'erp_leave' | 'official_trip';

interface ErpLeaveImportComparisonExternalRecord {
  leave_date: string | null;
  erp_leave_start_date?: string | null;
  erp_leave_end_date?: string | null;
  leave_period_code?: string | null;
  leave_period_name?: string | null;
}

interface ErpLeaveImportComparisonRow {
  id: string;
  sequence: number;
  systemRequest: ErpLeaveImportApprovedRequest | null;
  erpRequest: ErpLeaveImportComparisonExternalRecord | null;
  officialTripRequest: ErpLeaveImportComparisonExternalRecord | null;
  systemLeaveDate: string | null;
  sortDate: string | null;
  comparisonStatus: DetailComparisonStatus;
  comparisonLabel: string;
}

interface ErpLeaveImportComparisonDataSource extends Datatable {
  data: ErpLeaveImportComparisonRow[];
  offset: number;
}

@Component({
  selector: 'app-erp-leave-import',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
    FormImportNewComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './erp-leave-import.component.html',
  styleUrl: './erp-leave-import.component.scss',
})
export class ErpLeaveImportComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('summaryIndexTemplate', { static: true })
  summaryIndexTemplate!: TemplateRef<any>;
  @ViewChild('summaryTeacherTemplate', { static: true })
  summaryTeacherTemplate!: TemplateRef<any>;
  @ViewChild('summaryMatchCountTemplate', { static: true })
  summaryMatchCountTemplate!: TemplateRef<any>;
  @ViewChild('summaryUnmatchedCountTemplate', { static: true })
  summaryUnmatchedCountTemplate!: TemplateRef<any>;
  @ViewChild('summaryTotalCountTemplate', { static: true })
  summaryTotalCountTemplate!: TemplateRef<any>;
  @ViewChild('summaryImportedAtTemplate', { static: true })
  summaryImportedAtTemplate!: TemplateRef<any>;
  @ViewChild('leaveDateTemplate', { static: true })
  leaveDateTemplate!: TemplateRef<any>;
  @ViewChild('erpLeaveStartDateTemplate', { static: true })
  erpLeaveStartDateTemplate!: TemplateRef<any>;
  @ViewChild('erpLeaveEndDateTemplate', { static: true })
  erpLeaveEndDateTemplate!: TemplateRef<any>;
  @ViewChild('erpSubmittedAtTemplate', { static: true })
  erpSubmittedAtTemplate!: TemplateRef<any>;
  @ViewChild('erpLastUpdatedAtTemplate', { static: true })
  erpLastUpdatedAtTemplate!: TemplateRef<any>;
  @ViewChild('leaveTypeTemplate', { static: true })
  leaveTypeTemplate!: TemplateRef<any>;
  @ViewChild('usageStatusTemplate', { static: true })
  usageStatusTemplate!: TemplateRef<any>;
  @ViewChild('summaryActionTemplate', { static: true })
  summaryActionTemplate!: TemplateRef<any>;
  @ViewChild('comparisonIndexTemplate', { static: true })
  comparisonIndexTemplate!: TemplateRef<any>;
  @ViewChild('comparisonSystemRequestTemplate', { static: true })
  comparisonSystemRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonErpRequestTemplate', { static: true })
  comparisonErpRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonOfficialTripTemplate', { static: true })
  comparisonOfficialTripTemplate!: TemplateRef<any>;
  @ViewChild('comparisonLeaveDateTemplate', { static: true })
  comparisonLeaveDateTemplate!: TemplateRef<any>;
  @ViewChild('comparisonMakeupTeachingTemplate', { static: true })
  comparisonMakeupTeachingTemplate!: TemplateRef<any>;
  @ViewChild('comparisonStatusTemplate', { static: true })
  comparisonStatusTemplate!: TemplateRef<any>;
  @ViewChild(FormImportNewComponent)
  importForm?: FormImportNewComponent;
  @ViewChild('exportErrorDialog')
  exportErrorDialog?: ConfirmDialogComponent;
  @ViewChild('importSuccessDialog')
  importSuccessDialog?: ConfirmDialogComponent;
  @ViewChild('importExistingDataConfirmDialog')
  importExistingDataConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('importCheckErrorDialog')
  importCheckErrorDialog?: ConfirmDialogComponent;
  @ViewChild('publishConfirmDialog')
  publishConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('publishErrorDialog')
  publishErrorDialog?: ConfirmDialogComponent;
  @ViewChild('clearDataConfirmDialog')
  clearDataConfirmDialog?: ConfirmDialogComponent;
  @ViewChildren(DatatableComponent)
  datatables?: QueryList<DatatableComponent>;

  isImporting = false;
  isExporting = false;
  isCheckingExistingData = false;
  isPublishing = false;
  isClearingData = false;
  isLoadingDetail = false;
  isTeacherDetailTableReady = signal(false);
  isCheckResultDetailTableReady = signal(false);
  lastAction = signal<'export' | 'import' | null>(null);
  activeTab = signal<ErpLeaveImportTab>('leave_records');
  tableLoading = signal(false);
  leaveRecordTableLoading = signal(false);
  checkResultTableLoading = signal(false);
  erpImportBlocked = signal(true);
  publicationStatus = signal<ErpLeaveImportPublicationStatus | null>(null);
  publicationStatusLoading = signal(true);
  publicationStatusError = signal(false);
  selectedImportKind = signal<ErpLeaveImportKind>('erp_leave');
  pendingPublishImportKind: ErpLeaveImportKind | null = null;
  pendingClearData: { academicTerm: AcademicTermYearModel; importKind: ErpLeaveImportKind } | null = null;
  publishErrorMessage = 'ไม่สามารถ Public ข้อมูลได้ กรุณาลองใหม่อีกครั้ง';
  exportErrorMessage = 'ไม่สามารถดาวน์โหลดแบบฟอร์มได้ กรุณาลองใหม่อีกครั้ง';
  form!: FormGroup;
  selectedTeacherSummary?: ErpLeaveImportResultDetail;
  selectedCheckResultSummary?: ErpLeaveImportCheckSummaryRow;
  columns: any[] = [];
  leaveRecordColumns: any[] = [];
  comparisonColumns: any[] = [];
  checkResultComparisonColumns: any[] = [];
  readonly datatableColumnMode = ColumnMode;
  pageSizeOptions = [10, 20, 50];
  comparisonPageSizeOptions = [ 10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  teacherTableDatasource: ErpLeaveImportTeacherDataSource = this.createTeacherTableDatasource();
  leaveRecordTableDatasource: ErpLeaveImportLeaveRecordDataSource = this.createLeaveRecordTableDatasource();
  checkResultTableDatasource: ErpLeaveImportCheckDataSource = this.createCheckResultTableDatasource();
  checkResultDetailTableDatasource: ErpLeaveImportCheckDetailDataSource = this.createCheckResultDetailTableDatasource();
  comparisonTableDatasource: ErpLeaveImportComparisonDataSource = this.createComparisonTableDatasource();

  private erpLeaveImportService = inject(ErpLeaveImportService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private toastService = inject(ToastService);
  private cdr = inject(ChangeDetectorRef, { optional: true });
  private academicTerms: AcademicTermYearModel[] = [];
  private currentPage = 1;
  private currentLimit = 50;
  private leaveRecordCurrentPage = 1;
  private leaveRecordCurrentLimit = 50;
  private checkResultCurrentPage = 1;
  private checkResultCurrentLimit = 50;
  private checkResultDetailCurrentPage = 1;
  private checkResultDetailCurrentLimit = 50;
  private comparisonCurrentPage = 1;
  private comparisonCurrentLimit = 50;
  private tableRefreshTimers = new Set<ReturnType<typeof setTimeout>>();
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private leaveRecordCurrentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private checkResultCurrentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private readonly sortFieldMap = {
    index: 'imported_at',
    teacher: 'full_name',
    system_matched_request_count: 'system_matched_request_count',
    system_unmatched_request_count: 'system_unmatched_request_count',
    system_request_count: 'system_request_count',
    imported_at: 'imported_at',
  };
  private readonly leaveRecordSortFieldMap = {
    sequence: 'erp_leave_start_date',
    erp_person_number: 'erp_person_number',
    erp_person_name: 'erp_person_name',
    erp_full_name: 'erp_full_name',
    erp_department: 'erp_department',
    leave_type: 'leave_type',
    erp_leave_start_date: 'erp_leave_start_date',
    erp_leave_end_date: 'erp_leave_end_date',
    leave_period_name: 'leave_period_name',
    erp_submitted_at: 'erp_submitted_at',
    erp_last_updated_at: 'erp_last_updated_at',
    erp_approval_status: 'erp_approval_status',
    erp_adjustment_status: 'erp_adjustment_status',
  };

  get hasOnlyMatchedRows(): boolean {
    if (this.selectedTeacherSummary == null) {
      return false;
    }

    return this.selectedTeacherSummary.import_unmatched_count === 0
      && this.selectedTeacherSummary.system_unmatched_count === 0;
  }

  get isDocumentOfficer(): boolean {
    return this.authService.getActiveRoleId() === RoleID.DOCUMENT_OFFICER;
  }

  get isAdmin(): boolean {
    return this.authService.getActiveRoleId() === RoleID.ADMIN;
  }

  get activeImportKind(): ErpLeaveImportKind {
    if (this.isAdmin) {
      return this.selectedImportKind();
    }

    return this.isDocumentOfficer ? 'official_trip' : 'erp_leave';
  }

  get isOfficialTripSource(): boolean {
    return this.activeImportKind === 'official_trip';
  }

  get canShowClearDataTestButton(): boolean {
    return environment.enableErpLeaveImportTestClear && this.isAdmin;
  }

  get canClearSelectedData(): boolean {
    return this.canShowClearDataTestButton
      && this.getSelectedFilterAcademicTerm() != null
      && !this.isImporting
      && !this.isExporting
      && !this.isPublishing
      && !this.isCheckingExistingData
      && !this.isClearingData;
  }

  get firstTabLabel(): string {
    return this.isOfficialTripSource ? 'ข้อมูลไปปฏิบัติงาน' : 'ข้อมูลวันลา ERP';
  }

  get importRoleNotice(): string {
    if (this.isAdmin) {
      return 'แอดมินสามารถจัดการข้อมูลวันลา ERP และข้อมูลไปปฏิบัติงานได้ กรุณาเลือกแหล่งข้อมูล';
    }

    return this.isOfficialTripSource
      ? 'เจ้าหน้าที่สารบรรณสามารถนำเข้าได้เฉพาะข้อมูลไปปฏิบัติงาน'
      : 'นักทรัพยากรบุคคลสามารถนำเข้าได้เฉพาะข้อมูลวันลา ERP';
  }

  get importModalTitle(): string {
    return this.isOfficialTripSource ? 'นำเข้าข้อมูลไปปฏิบัติงาน' : 'นำเข้าวันลาจากระบบ ERP';
  }

  get checkResultSourceLabel(): string {
    return this.isOfficialTripSource ? 'ข้อมูลไปปฏิบัติงาน' : 'ข้อมูลวันลา ERP';
  }

  get importSuccessMessage(): string {
    return this.isOfficialTripSource ? 'นำเข้าข้อมูลไปปฏิบัติงานสำเร็จ' : 'นำเข้าข้อมูลวันลาจาก ERP สำเร็จ';
  }

  get existingDataTitle(): string {
    return this.isOfficialTripSource
      ? 'พบข้อมูลไปปฏิบัติงานในช่วงเวลาการศึกษาปัจจุบันแล้ว'
      : 'พบข้อมูลวันลา ERP ในช่วงเวลาการศึกษาปัจจุบันแล้ว';
  }

  get existingDataMessage(): string {
    const sourceLabel = this.isOfficialTripSource ? 'ข้อมูลไปปฏิบัติงาน' : 'ข้อมูลวันลา ERP';
    return `การนำเข้าใหม่จะแทนที่${sourceLabel}เดิมของช่วงเวลาการศึกษาปัจจุบัน โดยไม่กระทบข้อมูลอีกแหล่ง ต้องการดำเนินการต่อหรือไม่`;
  }

  get publishConfirmTitle(): string {
    return `ยืนยัน${this.importKindLabel(this.pendingPublishImportKind ?? this.activeImportKind)}`;
  }

  get publishConfirmMessage(): string {
    const sourceLabel = this.importKindLabel(this.pendingPublishImportKind ?? this.activeImportKind);
    return `หลังจาก ยืนยัน${sourceLabel}แล้ว จะไม่สามารถยกเลิกหรือนำเข้าข้อมูลแหล่งนี้ใหม่ได้ ต้องการดำเนินการต่อหรือไม่`;
  }

  get clearDataConfirmTitle(): string {
    const importKind = this.pendingClearData?.importKind ?? this.activeImportKind;
    return `ล้าง${this.importKindLabel(importKind)}`;
  }

  get clearDataConfirmMessage(): string {
    const pending = this.pendingClearData;
    if (pending == null) {
      return 'ต้องการล้างข้อมูลที่เลือกหรือไม่ การทำงานนี้มีผลจริงในฐานข้อมูล';
    }

    const year = this.toBuddhistYear(pending.academicTerm.year);
    return `ต้องการล้าง${this.importKindLabel(pending.importKind)} เทอม ${pending.academicTerm.name}/${year} หรือไม่ ระบบจะรีเซ็ตสถานะยืนยันแบบ manual และคำนวณผลรวมใหม่จากข้อมูลที่เหลือ การทำงานนี้มีผลจริงในฐานข้อมูล`;
  }

  get publicationBannerMessage(): string {
    const reason = this.publicationStatus()?.publish_reason;
    if (reason === 'disbursement_date') {
      return `${this.firstTabLabel}ยืนยันข้อมูลอัตโนมัติแล้ว — ถึงวันที่ส่งเบิกเทอมการศึกษานี้`;
    }

    return `${this.firstTabLabel}ยืนยันข้อมูลแล้ว — ไม่สามารถนำเข้าข้อมูลเพิ่มเติมได้`;
  }

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.initLeaveRecordColumns();
    this.initComparisonColumns();
    this.listenLeaveRecordFilters();
    this.loadAcademicTermOptions();
    this.loadPublicationStatus();
  }

  ngOnDestroy(): void {
    this.tableRefreshTimers.forEach((timer) => clearTimeout(timer));
    this.tableRefreshTimers.clear();
  }

  onExportExcel(): void {
    this.lastAction.set('export');

    if (this.isExporting || this.isClearingData) {
      return;
    }

    this.isExporting = true;
    const importKind = this.activeImportKind;
    this.erpLeaveImportService.exportTemplate(importKind).subscribe({
      next: (blob) => this.downloadBlob(
        blob,
        importKind === 'official_trip' ? 'official_trip_template.xlsx' : 'erp_leave_template.xlsx',
      ),
      error: (err) => {
        this.exportErrorMessage = this.getErrorMessage(err, 'ไม่สามารถดาวน์โหลดแบบฟอร์มได้ กรุณาลองใหม่อีกครั้ง');
        this.exportErrorDialog?.open();
        this.isExporting = false;
      },
      complete: () => {
        this.isExporting = false;
      },
    });
  }

  onImport(): void {
    this.lastAction.set('import');

    if (
      this.isImporting
      || this.isPublishing
      || this.isClearingData
      || this.isCheckingExistingData
      || this.publicationStatusLoading()
      || this.publicationStatusError()
      || this.erpImportBlocked()
    ) {
      return;
    }

    const importKind = this.activeImportKind;
    this.isCheckingExistingData = true;
    this.erpLeaveImportService.getLeaveRecords({
      ...this.buildCurrentAcademicTermFilterParams(),
      import_kind: importKind,
      page: 1,
      limit: 1,
      sort: null,
      direction: null,
    }).subscribe({
      next: (response) => {
        if (response.erp_import_blocked === true) {
          this.erpImportBlocked.set(true);
          this.loadPublicationStatus();
          return;
        }

        if (response.total > 0) {
          this.importExistingDataConfirmDialog?.open();
          return;
        }

        this.openImportModal();
      },
      error: () => {
        this.importCheckErrorDialog?.open();
        this.isCheckingExistingData = false;
      },
      complete: () => {
        this.isCheckingExistingData = false;
      },
    });
  }

  onConfirmImportExistingData(): void {
    this.importExistingDataConfirmDialog?.close();
    setTimeout(() => this.openImportModal(), 250);
  }

  onImportFile(file: File): void {
    this.lastAction.set('import');

    if (this.isImporting) {
      return;
    }

    this.isImporting = true;
    const importKind = this.activeImportKind;
    this.erpLeaveImportService.importLeaveRecords(file, importKind).subscribe({
      next: () => {
        this.importForm?.clearFileSelection();
        this.importForm?.closeAfterImportSuccess(() => {
          this.importSuccessDialog?.open();
        });
        this.resetToDefaultAcademicTermFilters();
        this.loadPublicationStatus();
        this.loadLeaveRecords(1);
        if (this.activeTab() === 'check_results') {
          this.loadCheckResultSummaries(1);
        } else if (this.activeTab() === 'match_results') {
          this.loadResults(1);
        }
      },
      error: (err) => {
        this.importForm?.showImportError(err.error);
        this.loadPublicationStatus();
        this.isImporting = false;
      },
      complete: () => {
        this.isImporting = false;
      },
    });
  }

  onImportKindChange(importKind: ErpLeaveImportKind): void {
    if (
      !this.isAdmin
      || this.selectedImportKind() === importKind
      || this.isImporting
      || this.isPublishing
      || this.isCheckingExistingData
      || this.isClearingData
    ) {
      return;
    }

    this.publishConfirmDialog?.close();
    this.pendingPublishImportKind = null;
    this.selectedImportKind.set(importKind);
    this.selectedTeacherSummary = undefined;
    this.selectedCheckResultSummary = undefined;
    this.leaveRecordCurrentPage = 1;
    this.checkResultCurrentPage = 1;
    this.checkResultDetailCurrentPage = 1;
    this.initLeaveRecordColumns();
    this.initComparisonColumns();
    this.loadPublicationStatus();

    if (this.activeTab() === 'leave_records') {
      this.loadLeaveRecords(1);
    } else if (this.activeTab() === 'check_results') {
      this.loadCheckResultSummaries(1);
    }
  }

  onPublish(): void {
    if (
      this.isPublishing
      || this.isImporting
      || this.isCheckingExistingData
      || this.isClearingData
      || this.publicationStatusLoading()
      || this.publicationStatusError()
      || this.publicationStatus()?.can_publish !== true
    ) {
      return;
    }

    this.pendingPublishImportKind = this.activeImportKind;
    this.publishConfirmDialog?.open();
  }

  onConfirmPublish(): void {
    if (this.isPublishing || this.isClearingData || this.publicationStatus()?.can_publish !== true) {
      return;
    }

    const importKind = this.pendingPublishImportKind ?? this.activeImportKind;
    this.isPublishing = true;
    this.erpLeaveImportService.publish(importKind).subscribe({
      next: (status) => {
        if (status.import_kind === this.activeImportKind) {
          this.applyPublicationStatus(status);
        } else {
          this.loadPublicationStatus();
        }
        this.pendingPublishImportKind = null;
        this.publishConfirmDialog?.close();
        this.pendingPublishImportKind = null;
      },
      error: (err) => {
        this.publishErrorMessage = this.getErrorMessage(
          err,
          'ไม่สามารถ Public ข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
        );
        this.publishConfirmDialog?.close();
        this.loadPublicationStatus();
        setTimeout(() => this.publishErrorDialog?.open(), 250);
        this.isPublishing = false;
      },
      complete: () => {
        this.isPublishing = false;
      },
    });
  }

  onClearData(): void {
    if (!this.canClearSelectedData) {
      return;
    }

    const academicTerm = this.getSelectedFilterAcademicTerm();
    if (academicTerm == null) {
      this.toastService.toastError('ไม่พบภาคการศึกษาที่เลือก');
      return;
    }

    this.pendingClearData = {
      academicTerm,
      importKind: this.activeImportKind,
    };
    this.clearDataConfirmDialog?.open();
  }

  onConfirmClearData(): void {
    const pending = this.pendingClearData;
    if (
      pending == null
      || !this.canShowClearDataTestButton
      || this.isClearingData
      || this.isImporting
      || this.isPublishing
    ) {
      return;
    }

    this.isClearingData = true;
    this.erpLeaveImportService.clearData(pending.academicTerm.id, pending.importKind).pipe(
      finalize(() => {
        this.isClearingData = false;
      }),
    ).subscribe({
      next: () => {
        const sourceLabel = this.importKindLabel(pending.importKind);
        this.clearDataConfirmDialog?.close();
        this.pendingClearData = null;
        this.selectedTeacherSummary = undefined;
        this.selectedCheckResultSummary = undefined;
        this.toastService.toastSuccess(`ล้าง${sourceLabel}สำเร็จ`);
        this.loadPublicationStatus();
        this.reloadActiveTableAfterClear();
      },
      error: (err) => {
        this.toastService.toastError(
          this.getErrorMessage(err, 'ไม่สามารถล้างข้อมูลได้ กรุณาลองใหม่อีกครั้ง'),
        );
      },
    });
  }

  onTabChange(tab: ErpLeaveImportTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.refreshVisibleDatatables();

    if (tab === 'match_results') {
      this.loadResults(1);
      return;
    }

    if (tab === 'check_results') {
      this.loadCheckResultSummaries(1);
      return;
    }

    this.loadLeaveRecords(1);
  }

  openSummaryDetail(row: ErpLeaveImportResultRow | ErpLeaveImportCheckSummaryRow): void {
    if (this.activeTab() === 'check_results') {
      this.openCheckResultDetail(row as ErpLeaveImportCheckSummaryRow);
      return;
    }

    this.openTeacherDetail(row as ErpLeaveImportResultRow);
  }

  openTeacherDetail(row: ErpLeaveImportResultRow): void {
    if (this.isLoadingDetail) {
      return;
    }

    this.isLoadingDetail = true;
    this.isTeacherDetailTableReady.set(false);
    this.comparisonCurrentPage = 1;
    this.erpLeaveImportService.getResult(row.id).subscribe({
      next: (detail) => {
        this.selectedTeacherSummary = detail;
        this.comparisonTableDatasource = this.createComparisonTableDatasource(detail);
        this.cdr?.detectChanges();
        this.openTeacherDetailModal();
      },
      error: () => {
        this.isLoadingDetail = false;
      },
      complete: () => {
        this.isLoadingDetail = false;
      },
    });
  }

  openCheckResultDetail(row: ErpLeaveImportCheckSummaryRow): void {
    if (this.isLoadingDetail || row.person_number == null || row.person_number === '') {
      return;
    }

    this.selectedCheckResultSummary = row;
    this.isCheckResultDetailTableReady.set(false);
    this.checkResultDetailCurrentPage = 1;
    this.checkResultDetailTableDatasource = this.createCheckResultDetailTableDatasource();
    this.loadCheckResultDetail(1, true);
  }

  createComparisonTableDatasource(
    teacher?: ErpLeaveImportResultDetail,
  ): ErpLeaveImportComparisonDataSource {
    const rows = teacher == null ? [] : this.buildComparisonRows(teacher);
    const limit = this.comparisonCurrentLimit;
    const totalPage = Math.max(Math.ceil(rows.length / limit), 1);
    const page = Math.min(Math.max(this.comparisonCurrentPage, 1), totalPage);
    const offset = (page - 1) * limit;
    const data = rows.slice(offset, offset + limit);
    this.comparisonCurrentPage = page;

    return {
      data,
      page,
      limit,
      total: rows.length,
      total_page: totalPage,
      offset,
    };
  }

  getUnsuccessfulComparisonCount(teacher: ErpLeaveImportResultDetail): number {
    return this.buildComparisonRows(teacher)
      .filter((row) => row.comparisonStatus !== 'matched')
      .length;
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.teacherTableDatasource.total_page || page === this.currentPage) {
      return;
    }

    this.loadResults(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadResults(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadResults(1);
  }

  onLeaveRecordPageChange(page: number): void {
    if (page < 1 || page > this.leaveRecordTableDatasource.total_page || page === this.leaveRecordCurrentPage) {
      return;
    }

    this.loadLeaveRecords(page);
  }

  onLeaveRecordLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.leaveRecordCurrentLimit) {
      return;
    }

    this.leaveRecordCurrentLimit = limit;
    this.loadLeaveRecords(1);
  }

  onLeaveRecordSortChange(event: DatatableSortChangeEvent): void {
    this.leaveRecordCurrentSortParams = createDatatableSortParams(event, this.leaveRecordSortFieldMap);
    this.loadLeaveRecords(1);
  }

  onCheckResultPageChange(page: number): void {
    if (page < 1 || page > this.checkResultTableDatasource.total_page || page === this.checkResultCurrentPage) {
      return;
    }

    this.loadCheckResultSummaries(page);
  }

  onCheckResultLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.checkResultCurrentLimit) {
      return;
    }

    this.checkResultCurrentLimit = limit;
    this.loadCheckResultSummaries(1);
  }

  onCheckResultSortChange(event: DatatableSortChangeEvent): void {
    this.checkResultCurrentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadCheckResultSummaries(1);
  }

  onCheckResultDetailPageChange(page: number): void {
    if (
      page < 1 ||
      page > this.checkResultDetailTableDatasource.total_page ||
      page === this.checkResultDetailCurrentPage
    ) {
      return;
    }

    this.loadCheckResultDetail(page);
  }

  onCheckResultDetailLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.checkResultDetailCurrentLimit) {
      return;
    }

    this.checkResultDetailCurrentLimit = limit;
    this.loadCheckResultDetail(1);
  }

  onComparisonPageChange(page: number): void {
    if (page < 1 || page > this.comparisonTableDatasource.total_page || page === this.comparisonCurrentPage) {
      return;
    }

    this.comparisonCurrentPage = page;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
  }

  onComparisonLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.comparisonCurrentLimit) {
      return;
    }

    this.comparisonCurrentLimit = limit;
    this.comparisonCurrentPage = 1;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
  }

  closeTeacherDetailModal(): void {
    const modalElement = document.getElementById('erpLeaveImportTeacherDetailModal');

    if (modalElement == null) {
      return;
    }

    bootstrap.Modal.getInstance(modalElement)?.hide();
  }

  closeCheckResultDetailModal(): void {
    const modalElement = document.getElementById('erpLeaveImportCheckResultDetailModal');

    if (modalElement == null) {
      return;
    }

    bootstrap.Modal.getInstance(modalElement)?.hide();
  }

  closeImportSuccessDialog(): void {
    this.importSuccessDialog?.close();
    this.cleanupImportModalState();
    setTimeout(() => this.cleanupImportModalState(), 600);
  }

  formatDate(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${day}/${month}/${Number(year) + 543}`;
  }

  formatTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    return value.slice(0, 5);
  }

  formatRequestTitle(request: ErpLeaveImportApprovedRequest): string {
    return [request.subject_code, request.subject_name].filter(Boolean).join(' - ') || '-';
  }

  formatRequestStatus(status: string | null | undefined): string {
    const labels: Record<string, string> = {
      approved: 'รับรองแล้ว',
      submitted: 'รอดำเนินการ',
      draft: 'รอดำเนินการ',
      rejected: 'ไม่อนุมัติ',
      revision_requested: 'ส่งกลับแก้ไข',
    };

    return status == null || status === '' ? '-' : labels[status] ?? status;
  }

  getRequestStatusBadgeClass(status: string | null | undefined): string {
    const classes: Record<string, string> = {
      approved: 'erp-leave-import-request-status-badge-success',
      submitted: 'erp-leave-import-request-status-badge-pending',
      draft: 'erp-leave-import-request-status-badge-pending',
      rejected: 'erp-leave-import-request-status-badge-danger',
      revision_requested: 'erp-leave-import-request-status-badge-revision',
    };

    return status == null || status === ''
      ? 'erp-leave-import-request-status-badge-neutral'
      : classes[status] ?? 'erp-leave-import-request-status-badge-neutral';
  }

  formatMakeupTime(request: ErpLeaveImportApprovedRequest): string {
    const date = this.formatDate(request.makeup_teaching_date);
    const timeRange = [request.makeup_start_time, request.makeup_end_time].filter(Boolean).join(' - ');

    return timeRange !== '' ? `${date} ${timeRange}` : date;
  }

  buildComparisonRows(teacher: ErpLeaveImportResultDetail): ErpLeaveImportComparisonRow[] {
    const rows: Omit<ErpLeaveImportComparisonRow, 'sequence'>[] = [];

    teacher.matched.forEach((erpRow) => {
      (erpRow.approved_requests ?? []).forEach((request) => {
        rows.push({
          id: `matched-${erpRow.leave_record_id ?? erpRow.schedule_submission_id}-${request.id}`,
          systemRequest: request,
          erpRequest: erpRow.erp_record ?? (erpRow.leave_record_id != null ? erpRow : null),
          officialTripRequest: erpRow.official_trip_record ?? null,
          systemLeaveDate: request.leave_date !== '' ? request.leave_date : null,
          sortDate: request.leave_date || erpRow.leave_date,
          comparisonStatus: 'matched',
          comparisonLabel: 'ตรงกัน',
        });
      });
    });

    teacher.system_unmatched
      .filter((request) => this.isApprovedRequest(request))
      .forEach((request) => {
        const status = request.comparison_status ?? 'missing_both_external';
        rows.push({
          id: `system-${request.id}`,
          systemRequest: request,
          erpRequest: request.erp_record ?? null,
          officialTripRequest: request.official_trip_record ?? null,
          systemLeaveDate: request.leave_date !== '' ? request.leave_date : null,
          sortDate: request.leave_date !== '' ? request.leave_date : null,
          comparisonStatus: status,
          comparisonLabel: this.comparisonLabel(status, teacher.matching_rule_version),
        });
      });

    return this.sequenceComparisonRows(rows);
  }

  formatComparisonDateTime(row: ErpLeaveImportComparisonRow): string {
    const leaveDate = this.formatDate(row.systemLeaveDate);

    if (row.systemRequest == null) {
      return leaveDate;
    }

    const makeupTime = this.formatMakeupTime(row.systemRequest);
    return makeupTime !== '-' ? `${leaveDate}\n${makeupTime}` : leaveDate;
  }

  formatLeaveDate(row: ErpLeaveImportComparisonRow): string {
    return this.formatDate(row.systemLeaveDate);
  }

  formatErpLeaveDate(row: ErpLeaveImportComparisonRow): string {
    return this.formatErpDateRange(row.erpRequest);
  }

  formatOfficialTripDate(row: ErpLeaveImportComparisonRow): string {
    return this.formatErpDateRange(row.officialTripRequest);
  }

  formatLeavePeriod(value: { leave_period_name?: string | null } | null | undefined): string {
    return value?.leave_period_name?.trim() || '-';
  }

  formatErpDateRange(
    row: ErpLeaveImportComparisonExternalRecord | ErpLeaveImportLeaveRecordRow | null | undefined,
  ): string {
    const fallbackLeaveDate = row?.leave_date;
    const startValue = row?.erp_leave_start_date != null && row.erp_leave_start_date !== ''
      ? row.erp_leave_start_date
      : fallbackLeaveDate;
    const endValue = row?.erp_leave_end_date != null && row.erp_leave_end_date !== ''
      ? row.erp_leave_end_date
      : fallbackLeaveDate;
    const startDate = this.formatDate(startValue);
    const endDate = this.formatDate(endValue);

    if (startDate === '-' && endDate === '-') {
      return '-';
    }

    if (startDate === endDate || endDate === '-') {
      return startDate;
    }

    if (startDate === '-') {
      return endDate;
    }

    return `${startDate} - ${endDate}`;
  }

  formatLeaveBoundary(date: string | null | undefined, time: string | null | undefined): string {
    const formattedDate = this.formatDate(date);
    const formattedTime = this.formatTime(time);
    return formattedTime === '-' ? formattedDate : `${formattedDate} ${formattedTime}`;
  }

  formatMakeupTeachingDate(row: ErpLeaveImportComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    return this.formatDate(row.systemRequest.makeup_teaching_date);
  }

  formatMakeupTeachingTimeWithHours(row: ErpLeaveImportComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const timeRange = [row.systemRequest.makeup_start_time, row.systemRequest.makeup_end_time]
      .filter(Boolean)
      .join(' - ');

    if (timeRange === '') {
      return '-';
    }

    const hours = this.formatMakeupHours(row);
    return hours === '-' ? timeRange : `${timeRange} (${hours})`;
  }

  formatMakeupHours(row: ErpLeaveImportComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const startMinutes = this.timeToMinutes(row.systemRequest.makeup_start_time);
    const endMinutes = this.timeToMinutes(row.systemRequest.makeup_end_time);

    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      return '-';
    }

    return this.formatHours((endMinutes - startMinutes) / 60);
  }

  shouldShowMakeupTeaching(row: ErpLeaveImportComparisonRow): boolean {
    return row.systemRequest != null;
  }

  getComparisonBadgeClass(status: DetailComparisonStatus): string {
    const classes: Record<DetailComparisonStatus, string> = {
      matched: 'erp-leave-import-comparison-badge-success',
      missing_erp: 'erp-leave-import-comparison-badge-danger',
      missing_official_trip: 'erp-leave-import-comparison-badge-danger',
      missing_both_external: 'erp-leave-import-comparison-badge-danger',
      not_approved: 'erp-leave-import-comparison-badge-danger',
      missing_approved_request: 'erp-leave-import-comparison-badge-danger',
      source_unmatched: 'erp-leave-import-comparison-badge-danger',
    };

    return classes[status];
  }

  trackByImportRow(_index: number, row: ErpLeaveImportRowResult): string {
    return `${row.schedule_submission_id}-${row.leave_date}-${row.row}`;
  }

  trackByApprovedRequest(_index: number, request: ErpLeaveImportApprovedRequest): number {
    return request.id;
  }

  trackByComparisonRow(_index: number, row: ErpLeaveImportComparisonRow): string {
    return row.id;
  }

  formatDateTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  formatRowNumber(value: number | null | undefined): number | string {
    return value ?? '-';
  }

  formatIsActive(value: boolean | null | undefined): string {
    if (value === true) {
      return 'ใช้งาน';
    }

    if (value === false) {
      return 'ยกเลิก';
    }

    return '-';
  }

  getIsActiveBadgeClass(value: boolean | null | undefined): string {
    if (value === true) {
      return 'erp-leave-import-status-badge-active';
    }

    if (value === false) {
      return 'erp-leave-import-status-badge-inactive';
    }

    return 'erp-leave-import-status-badge-empty';
  }

  formatHours(value: number): string {
    const formattedValue = Number.isInteger(value)
      ? String(value)
      : value.toFixed(2).replace(/\.?0+$/, '');

    return `${formattedValue} ชม.`;
  }

  loadResults(page = this.currentPage): void {
    this.tableLoading.set(true);
    this.currentPage = page;

    this.erpLeaveImportService.getResults({
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (response) => {
        this.teacherTableDatasource = this.createTeacherTableDatasource(response);
        this.tableLoading.set(false);
        this.refreshVisibleDatatables();
      },
      error: () => {
        this.teacherTableDatasource = this.createTeacherTableDatasource();
        this.tableLoading.set(false);
        this.refreshVisibleDatatables();
      },
    });
  }

  loadPublicationStatus(): void {
    const importKind = this.activeImportKind;
    this.publicationStatusLoading.set(true);
    this.publicationStatusError.set(false);
    this.erpImportBlocked.set(true);

    this.erpLeaveImportService.getPublicationStatus(importKind).subscribe({
      next: (status) => {
        if (status.import_kind !== this.activeImportKind) {
          return;
        }

        this.applyPublicationStatus(status);
      },
      error: () => {
        if (importKind !== this.activeImportKind) {
          return;
        }

        this.publicationStatus.set(null);
        this.publicationStatusError.set(true);
        this.publicationStatusLoading.set(false);
        this.erpImportBlocked.set(true);
      },
    });
  }

  loadLeaveRecords(page = this.leaveRecordCurrentPage): void {
    this.leaveRecordTableLoading.set(true);
    this.leaveRecordCurrentPage = page;

    this.erpLeaveImportService.getLeaveRecords({
      ...this.buildLeaveRecordFilterParams(),
      import_kind: this.activeImportKind,
      page: this.leaveRecordCurrentPage,
      limit: this.leaveRecordCurrentLimit,
      sort: this.leaveRecordCurrentSortParams.sort,
      direction: this.leaveRecordCurrentSortParams.direction,
    }).subscribe({
      next: (response) => {
        this.leaveRecordTableDatasource = this.createLeaveRecordTableDatasource(response);
        this.leaveRecordTableLoading.set(false);
        this.refreshVisibleDatatables();
      },
      error: () => {
        this.leaveRecordTableDatasource = this.createLeaveRecordTableDatasource();
        this.leaveRecordTableLoading.set(false);
        this.refreshVisibleDatatables();
      },
    });
  }

  loadCheckResultSummaries(page = this.checkResultCurrentPage): void {
    this.checkResultTableLoading.set(true);
    this.checkResultCurrentPage = page;

    this.erpLeaveImportService.getCheckResultSummaries({
      ...this.buildLeaveRecordFilterParams(),
      import_kind: this.activeImportKind,
      page,
      limit: this.checkResultCurrentLimit,
      sort: this.checkResultCurrentSortParams.sort,
      direction: this.checkResultCurrentSortParams.direction,
    }).subscribe({
      next: (response) => {
        this.checkResultTableDatasource = this.createCheckResultTableDatasource(response);
        this.checkResultTableLoading.set(false);
        this.refreshVisibleDatatables();
      },
      error: () => {
        this.checkResultTableDatasource = this.createCheckResultTableDatasource();
        this.checkResultTableLoading.set(false);
        this.refreshVisibleDatatables();
      },
    });
  }

  loadCheckResultDetail(page = this.checkResultDetailCurrentPage, openModal = false): void {
    const personNumber = this.selectedCheckResultSummary?.person_number;

    if (personNumber == null || personNumber === '') {
      this.checkResultDetailTableDatasource = this.createCheckResultDetailTableDatasource();
      return;
    }

    this.isLoadingDetail = true;
    this.checkResultDetailCurrentPage = page;
    this.erpLeaveImportService.getCheckResults({
      ...this.buildLeaveRecordFilterParams(),
      import_kind: this.activeImportKind,
      person_number: personNumber,
      page,
      limit: this.checkResultDetailCurrentLimit,
    }).subscribe({
      next: (response) => {
        this.checkResultDetailTableDatasource = this.createCheckResultDetailTableDatasource(response);
        this.cdr?.detectChanges();
        if (openModal) {
          this.openCheckResultDetailModal();
        } else {
          this.refreshVisibleDatatables();
        }
      },
      error: () => {
        this.checkResultDetailTableDatasource = this.createCheckResultDetailTableDatasource();
        this.isLoadingDetail = false;
      },
      complete: () => {
        this.isLoadingDetail = false;
      },
    });
  }

  private refreshVisibleDatatables(): void {
    [0, 100].forEach((delay) => {
      const timer = setTimeout(() => {
        this.tableRefreshTimers.delete(timer);
        this.datatables?.forEach((datatable) => datatable.refreshLayout());
      }, delay);

      this.tableRefreshTimers.add(timer);
    });
  }

  private initColumns(): void {
    const dataCellClass = 'erp-leave-import-summary-data-cell';
    const dataHeaderClass = 'erp-leave-import-summary-data-header';

    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.summaryIndexTemplate,
        frozenLeft: true,
        canAutoResize: false,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: dataCellClass,
        headerClass: dataHeaderClass,
      },
      {
        prop: 'teacher',
        name: 'ชื่อ-นามสกุล อาจารย์ / อีเมล',
        sortable: true,
        cellTemplate: this.summaryTeacherTemplate,
        canAutoResize: true,
        width: 320,
        minWidth: 300,
        cellClass: dataCellClass,
        headerClass: dataHeaderClass,
      },
      {
        prop: 'system_matched_request_count',
        name: 'รายการคำขอที่ตรงกัน',
        sortable: true,
        cellTemplate: this.summaryMatchCountTemplate,
        canAutoResize: true,
        width: 205,
        minWidth: 205,
        cellClass: dataCellClass,
        headerClass: dataHeaderClass,
      },
      {
        prop: 'system_unmatched_request_count',
        name: 'รายการคำขอที่ไม่ตรงกัน',
        sortable: true,
        cellTemplate: this.summaryUnmatchedCountTemplate,
        canAutoResize: true,
        width: 230,
        minWidth: 230,
        cellClass: dataCellClass,
        headerClass: dataHeaderClass,
      },
      {
        prop: 'system_request_count',
        name: 'รายการคำขอทั้งหมด',
        sortable: true,
        cellTemplate: this.summaryTotalCountTemplate,
        canAutoResize: true,
        width: 190,
        minWidth: 190,
        cellClass: dataCellClass,
        headerClass: dataHeaderClass,
      },
      {
        prop: 'imported_at',
        name: 'วันที่นำเข้าล่าสุด',
        sortable: true,
        cellTemplate: this.summaryImportedAtTemplate,
        canAutoResize: true,
        width: 190,
        minWidth: 190,
        cellClass: dataCellClass,
        headerClass: dataHeaderClass,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.summaryActionTemplate,
        frozenRight: true,
        canAutoResize: false,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'erp-leave-import-summary-action-cell',
        headerClass: 'erp-leave-import-summary-action-header',
      },
    ];
  }

  private initLeaveRecordColumns(): void {
    this.leaveRecordColumns = [
      {
        prop: 'sequence',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'erp_person_number',
        name: 'PERSON_NUMBER',
        sortable: true,
        width: 150,
        minWidth: 150,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'erp_person_name',
        name: 'PERSON_NAME',
        sortable: true,
        width: 220,
        minWidth: 220,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'erp_full_name',
        name: 'FULL_NAME',
        sortable: true,
        width: 220,
        minWidth: 220,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'erp_department',
        name: 'DEPARTMENT',
        sortable: true,
        width: 300,
        minWidth: 300,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'leave_type',
        name: 'LEAVE_TYPE',
        sortable: true,
        cellTemplate: this.leaveTypeTemplate,
        width: 220,
        minWidth: 220,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'erp_leave_start_date',
        name: 'LEAVE_START_DATE',
        sortable: true,
        cellTemplate: this.erpLeaveStartDateTemplate,
        width: 170,
        minWidth: 170,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'erp_leave_end_date',
        name: 'LEAVE_END_DATE',
        sortable: true,
        cellTemplate: this.erpLeaveEndDateTemplate,
        width: 160,
        minWidth: 160,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'erp_submitted_at',
        name: 'SUBMITTED_DATE',
        sortable: true,
        cellTemplate: this.erpSubmittedAtTemplate,
        width: 210,
        minWidth: 210,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'erp_last_updated_at',
        name: 'LAST_UPDATED_DATE',
        sortable: true,
        cellTemplate: this.erpLastUpdatedAtTemplate,
        width: 210,
        minWidth: 210,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'erp_approval_status',
        name: 'APPROVAL_STATUS',
        sortable: true,
        width: 180,
        minWidth: 180,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'erp_adjustment_status',
        name: 'ADJUSTMENT_STATUS',
        sortable: true,
        width: 200,
        minWidth: 200,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
    ];

    if (this.isOfficialTripSource) {
      const endDateColumnIndex = this.leaveRecordColumns.findIndex(
        (column) => column.prop === 'erp_leave_end_date',
      );
      this.leaveRecordColumns.splice(endDateColumnIndex + 1, 0, {
        prop: 'leave_period_name',
        name: 'LEAVE_PERIOD',
        sortable: true,
        width: 130,
        minWidth: 130,
        cellClass: 'erp-leave-import-text-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      });
      const officialTripColumns = new Set([
        'sequence',
        'erp_person_number',
        'erp_person_name',
        'erp_leave_start_date',
        'erp_leave_end_date',
        'leave_period_name',
      ]);
      this.leaveRecordColumns = this.leaveRecordColumns.filter((column) => officialTripColumns.has(column.prop));
    }
  }

  private initComparisonColumns(): void {
    this.comparisonColumns = this.createComparisonColumns('combined');
    this.checkResultComparisonColumns = this.createComparisonColumns(
      this.isOfficialTripSource ? 'official_trip' : 'erp_leave',
    );
  }

  private createComparisonColumns(scope: ErpLeaveImportComparisonColumnScope): any[] {
    const columns = [
      {
        prop: 'sequence',
        name: '#',
        sortable: false,
        cellTemplate: this.comparisonIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'systemRequest',
        name: 'คำขอชดเชยจากระบบ',
        sortable: false,
        cellTemplate: this.comparisonSystemRequestTemplate,
        width: 330,
        minWidth: 300,
        flexGrow: 2,
        cellClass: 'erp-leave-import-comparison-detail-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      },
      {
        prop: 'systemLeaveDate',
        name: 'วันที่ขอลา/หยุด',
        sortable: false,
        cellTemplate: this.comparisonLeaveDateTemplate,
        width: 132,
        minWidth: 120,
        flexGrow: 1,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
    ];

    if (scope !== 'official_trip') {
      columns.push({
        prop: 'erpRequest',
        name: 'วันที่ขอลาจาก ERP',
        sortable: false,
        cellTemplate: this.comparisonErpRequestTemplate,
        width: 170,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'erp-leave-import-comparison-detail-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      });
    }

    if (scope !== 'erp_leave') {
      columns.push({
        prop: 'officialTripRequest',
        name: 'วันที่ไปปฏิบัติงาน',
        sortable: false,
        cellTemplate: this.comparisonOfficialTripTemplate,
        width: 170,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'erp-leave-import-comparison-detail-cell erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-text-header erp-leave-import-left-header',
      });
    }

    columns.push(
      {
        prop: 'makeupTeachingDate',
        name: 'วันขอสอนชดเชย',
        sortable: false,
        cellTemplate: this.comparisonMakeupTeachingTemplate,
        width: 250,
        minWidth: 220,
        flexGrow: 1,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
      {
        prop: 'comparisonStatus',
        name: 'ผลการเปรียบเทียบ',
        sortable: false,
        cellTemplate: this.comparisonStatusTemplate,
        width: 170,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'erp-leave-import-left-cell',
        headerClass: 'erp-leave-import-left-header',
      },
    );

    return columns;
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private compareComparisonRows(
    left: Omit<ErpLeaveImportComparisonRow, 'sequence'>,
    right: Omit<ErpLeaveImportComparisonRow, 'sequence'>,
  ): number {
    const leftStatusOrder = this.comparisonStatusSortOrder(left.comparisonStatus);
    const rightStatusOrder = this.comparisonStatusSortOrder(right.comparisonStatus);

    if (leftStatusOrder !== rightStatusOrder) {
      return leftStatusOrder - rightStatusOrder;
    }

    const leftDate = left.sortDate ?? '';
    const rightDate = right.sortDate ?? '';

    if (leftDate !== rightDate) {
      return leftDate.localeCompare(rightDate);
    }

    return left.id.localeCompare(right.id);
  }

  private sequenceComparisonRows(
    rows: Omit<ErpLeaveImportComparisonRow, 'sequence'>[],
  ): ErpLeaveImportComparisonRow[] {
    return rows
      .sort((left, right) => this.compareComparisonRows(left, right))
      .map((row, index) => ({
        ...row,
        sequence: index + 1,
      }));
  }

  private isApprovedRequest(request: ErpLeaveImportApprovedRequest): boolean {
    return request.status === 'approved';
  }

  private comparisonStatusSortOrder(status: DetailComparisonStatus): number {
    const order: Record<DetailComparisonStatus, number> = {
      matched: 0,
      missing_approved_request: 1,
      missing_erp: 2,
      missing_official_trip: 3,
      missing_both_external: 4,
      not_approved: 5,
      source_unmatched: 6,
    };

    return order[status];
  }

  private comparisonLabel(status: ErpLeaveImportComparisonStatus, matchingRuleVersion?: number): string {
    const labels: Record<ErpLeaveImportComparisonStatus, string> = {
      matched: 'ตรงกัน',
      not_approved: 'ยังไม่รับรอง',
      missing_erp: 'ไม่พบใน ERP',
      missing_official_trip: 'ไม่พบข้อมูลไปปฏิบัติงาน',
      missing_both_external: matchingRuleVersion != null && matchingRuleVersion >= 3
        ? 'ไม่ตรง'
        : 'ไม่พบข้อมูล ERP และไปปฏิบัติงาน',
    };

    return labels[status];
  }

  private listenLeaveRecordFilters(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        if (this.activeTab() === 'check_results') {
          this.loadCheckResultSummaries(1);
        } else if (this.activeTab() === 'match_results') {
          this.loadResults(1);
        } else {
          this.loadLeaveRecords(1);
        }
      });
  }

  private loadAcademicTermOptions(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
        next: (terms) => {
          this.academicTerms = terms;
          const academicTermOptions = this.buildAcademicTermOptions(terms);
          const yearOptions = this.buildAcademicYearOptions(terms);

          this.academicTermOptions = academicTermOptions.length > 0 ? academicTermOptions : this.academicTermOptions;
          this.yearOptions = yearOptions.length > 0 ? yearOptions : this.yearOptions;
          this.ensureAcademicTermFilterValues();
          this.loadLeaveRecords(1);
        },
        error: () => this.loadLeaveRecords(1),
      });
  }

  private buildLeaveRecordFilterParams(): Pick<
    ErpLeaveImportLeaveRecordsParams,
    'academic_term_id' | 'academic_term_name' | 'academic_term_year'
  > {
    const { academicTerm, year } = this.form.getRawValue();
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const selectedTerm = this.findAcademicTerm(normalizedAcademicTerm, normalizedYear);
    const params: Pick<
      ErpLeaveImportLeaveRecordsParams,
      'academic_term_id' | 'academic_term_name' | 'academic_term_year'
    > = {};

    if (selectedTerm != null) {
      params.academic_term_id = selectedTerm.id;
      return params;
    }

    if (normalizedAcademicTerm !== '') {
      params.academic_term_name = normalizedAcademicTerm;
    }

    const numericYear = Number(normalizedYear);
    if (Number.isFinite(numericYear) && numericYear > 0) {
      params.academic_term_year = this.toChristianYear(numericYear);
    }

    return params;
  }

  private getSelectedFilterAcademicTerm(): AcademicTermYearModel | undefined {
    const { academicTerm, year } = this.form.getRawValue();
    return this.findAcademicTerm(
      String(academicTerm ?? '').trim(),
      String(year ?? '').trim(),
    );
  }

  private reloadActiveTableAfterClear(): void {
    if (this.activeTab() === 'check_results') {
      this.loadCheckResultSummaries(1);
      return;
    }

    if (this.activeTab() === 'match_results') {
      this.loadResults(1);
      return;
    }

    this.loadLeaveRecords(1);
  }

  private buildCurrentAcademicTermFilterParams(): Pick<
    ErpLeaveImportLeaveRecordsParams,
    'academic_term_id' | 'academic_term_name' | 'academic_term_year'
  > {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();
    const currentTerm = this.findAcademicTerm(defaultAcademicTerm, defaultYear);

    if (currentTerm != null) {
      return { academic_term_id: currentTerm.id };
    }

    return {
      academic_term_name: defaultAcademicTerm,
      academic_term_year: this.toChristianYear(Number(defaultYear)),
    };
  }

  private resetToDefaultAcademicTermFilters(): void {
    this.form.patchValue({
      academicTerm: this.getDefaultAcademicTermName(),
      year: this.getDefaultBuddhistYear(),
    }, { emitEvent: false });
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const currentYear = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === academicTermName &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private toBuddhistYear(year: number): number {
    return year > 2400 ? year : year + 543;
  }

  private toChristianYear(year: number): number {
    return year > 2400 ? year - 543 : year;
  }

  private timeToMinutes(value: string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const [hour, minute] = value.split(':').map((part) => Number(part));

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    return (hour * 60) + minute;
  }

  private createTeacherTableDatasource(response?: ErpLeaveImportResultsResponse): ErpLeaveImportTeacherDataSource {
    return {
      data: response?.data ?? [],
      limit: response?.limit ?? this.currentLimit,
      offset: response?.offset ?? ((response?.page ?? 1) - 1) * (response?.limit ?? this.currentLimit),
      page: response?.page ?? 1,
      total: response?.total ?? 0,
      total_page: response?.total_page ?? response?.total_pages ?? 1,
    };
  }

  private createLeaveRecordTableDatasource(response?: ErpLeaveImportLeaveRecordsResponse): ErpLeaveImportLeaveRecordDataSource {
    return {
      data: response?.data ?? [],
      limit: response?.limit ?? this.leaveRecordCurrentLimit,
      offset: response?.offset ?? ((response?.page ?? 1) - 1) * (response?.limit ?? this.leaveRecordCurrentLimit),
      page: response?.page ?? 1,
      total: response?.total ?? 0,
      total_page: response?.total_page ?? response?.total_pages ?? 1,
    };
  }

  private createCheckResultTableDatasource(
    response?: ErpLeaveImportCheckResultSummariesResponse,
  ): ErpLeaveImportCheckDataSource {
    return {
      data: response?.data ?? [],
      limit: response?.limit ?? this.checkResultCurrentLimit,
      offset: response?.offset ?? ((response?.page ?? 1) - 1) * (response?.limit ?? this.checkResultCurrentLimit),
      page: response?.page ?? 1,
      total: response?.total ?? 0,
      total_page: response?.total_page ?? response?.total_pages ?? 1,
    };
  }

  private createCheckResultDetailTableDatasource(
    response?: ErpLeaveImportCheckResultsResponse,
  ): ErpLeaveImportCheckDetailDataSource {
    return {
      data: (response?.data ?? []).map((row) => this.mapCheckResultComparisonRow(row)),
      limit: response?.limit ?? this.checkResultDetailCurrentLimit,
      offset: response?.offset ?? ((response?.page ?? 1) - 1) * (response?.limit ?? this.checkResultDetailCurrentLimit),
      page: response?.page ?? 1,
      total: response?.total ?? 0,
      total_page: response?.total_page ?? response?.total_pages ?? 1,
    };
  }

  private mapCheckResultComparisonRow(row: ErpLeaveImportCheckResultRow): ErpLeaveImportComparisonRow {
    const importedRecord: ErpLeaveImportComparisonExternalRecord | null =
      row.leave_start_date == null && row.leave_end_date == null
        ? null
        : {
            leave_date: row.leave_start_date ?? row.leave_end_date,
            erp_leave_start_date: row.leave_start_date,
            erp_leave_end_date: row.leave_end_date,
            leave_period_code: row.leave_period_code,
            leave_period_name: row.leave_period_name,
          };
    const sourceMatched = row.matched;

    return {
      id: row.id,
      sequence: row.sequence,
      systemRequest: row.system_request,
      erpRequest: row.import_kind === 'erp_leave' ? importedRecord : null,
      officialTripRequest: row.import_kind === 'official_trip' ? importedRecord : null,
      systemLeaveDate: row.system_request?.leave_date ?? null,
      sortDate: row.system_request?.leave_date ?? row.leave_start_date ?? row.leave_end_date,
      comparisonStatus: sourceMatched ? 'matched' : 'source_unmatched',
      comparisonLabel: sourceMatched ? 'ตรงกัน' : 'ไม่ตรงกัน',
    };
  }

  private openTeacherDetailModal(): void {
    const modalElement = document.getElementById('erpLeaveImportTeacherDetailModal');

    if (modalElement == null) {
      return;
    }

    modalElement.addEventListener('shown.bs.modal', () => {
      this.isTeacherDetailTableReady.set(true);

      if (this.selectedTeacherSummary != null) {
        this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
      }

      this.cdr?.detectChanges();
    }, { once: true });

    modalElement.addEventListener('hidden.bs.modal', () => {
      this.isTeacherDetailTableReady.set(false);
      this.cdr?.detectChanges();
    }, { once: true });

    bootstrap.Modal.getOrCreateInstance(modalElement, {
      backdrop: 'static',
      keyboard: false,
    }).show();
  }

  private openCheckResultDetailModal(): void {
    const modalElement = document.getElementById('erpLeaveImportCheckResultDetailModal');

    if (modalElement == null) {
      return;
    }

    modalElement.addEventListener('shown.bs.modal', () => {
      this.isCheckResultDetailTableReady.set(true);
      this.cdr?.detectChanges();
      this.refreshVisibleDatatables();
    }, { once: true });

    modalElement.addEventListener('hidden.bs.modal', () => {
      this.isCheckResultDetailTableReady.set(false);
      this.cdr?.detectChanges();
    }, { once: true });

    bootstrap.Modal.getOrCreateInstance(modalElement, {
      backdrop: 'static',
      keyboard: false,
    }).show();
  }

  private openImportModal(): void {
    this.importForm?.openModal();
  }

  private applyPublicationStatus(status: ErpLeaveImportPublicationStatus): void {
    this.publicationStatus.set(status);
    this.erpImportBlocked.set(status.published);
    this.publicationStatusError.set(false);
    this.publicationStatusLoading.set(false);
  }

  private importKindLabel(importKind: ErpLeaveImportKind): string {
    return importKind === 'official_trip' ? 'ข้อมูลไปปฏิบัติงาน' : 'ข้อมูลวันลา ERP';
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(url);
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const responseError = (error as { error?: unknown } | null | undefined)?.error;

    if (responseError != null && typeof responseError === 'object') {
      const errorText = (responseError as { error?: unknown; message?: unknown }).error;
      const messageText = (responseError as { error?: unknown; message?: unknown }).message;
      const message = this.getText(errorText) || this.getText(messageText);

      return message || fallback;
    }

    return this.getText(responseError) || this.getText((error as { message?: unknown } | null | undefined)?.message) || fallback;
  }

  private getText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private cleanupImportModalState(): void {
    [
      'erpLeaveImportDataModal',
      'erpLeaveImportDataModalLoading',
      'erpLeaveImportDataModalError',
      'erpLeaveImportSuccess',
    ].forEach((modalId) => this.forceHideModal(modalId));

    if (document.querySelector('.modal.show') != null) {
      return;
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }

  private forceHideModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);

    if (modalElement == null) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && modalElement.contains(activeElement)) {
      activeElement.blur();
    }

    bootstrap.Modal.getInstance(modalElement)?.hide();
    modalElement.classList.remove('show');
    modalElement.setAttribute('aria-hidden', 'true');
    modalElement.removeAttribute('aria-modal');
    modalElement.removeAttribute('role');
    modalElement.style.display = 'none';
  }
}
